import type { ApolloCache } from "@apollo/client";
import { RevisionIntent } from "@shuttlerock/api-types/federated";
import { DeliverableRevisionsCountFragment } from "../queries/deliverable-revisions-count.graphql";

const deliverableCacheId = (cache: ApolloCache, deliverableKey: string) =>
  cache.identify({ __typename: "Deliverable", key: deliverableKey });

// The corrective revision count the cache holds for a deliverable, or
// undefined when no document has landed it yet. The fragment names the count
// alone, so a deliverable cached by a document that carries no other revision
// fact (the version list's DeliverableDetails, the review panel's tree) still
// answers — an all-or-nothing read of a wider fragment would answer nothing,
// and the re-assertion below would then stack its +1 on a refetch that had
// already landed the server's count.
export const readDeliverableRevisionCount = (
  cache: ApolloCache,
  deliverableKey: string,
): number | undefined => {
  const id = deliverableCacheId(cache, deliverableKey);
  if (!id) {
    return undefined;
  }

  const count = cache.readFragment({ id, fragment: DeliverableRevisionsCountFragment })?.revisions
    .count;
  return typeof count === "number" ? count : undefined;
};

// The cached revision facts are re-asserted after a CORRECTIVE revision
// request: the count moves to at least one past `previousRevisionsCount` —
// the count read before the mutation (readDeliverableRevisionCount) — and a
// concept-wide request marks the deliverable. Both are the deliverable's
// corrective facts — the revision subgraph resolves `Deliverable.revisions`
// and `hasConceptRevisions` with the corrective intent alone — so a
// variation request, whose revision belongs to the draft specification
// (`DeliverableSpecification.revisions`, carried by the refetch and the
// Specification append), leaves them untouched. The refetch that follows
// the mutation may already have landed the server's count; the baseline
// keeps the two from stacking. With no baseline (no document had landed the
// count before the request) the count stays as the post-commit refetch or
// the list append wrote it: an increment relative to a count a refetch may
// have refreshed is a double count. The modifier runs for every cached
// `revisions` field on the entity whatever its arguments, so every document
// sharing the deliverable sees the move.
export const updateCacheOnRequestRevision = (
  cache: ApolloCache,
  deliverableKey: string,
  intent: RevisionIntent,
  appliesToConcept: boolean,
  previousRevisionsCount: number | undefined,
) => {
  if (intent !== RevisionIntent.Corrective) {
    return;
  }

  const id = deliverableCacheId(cache, deliverableKey);

  if (!id) {
    return;
  }

  cache.modify<{ revisions: { count?: number }; hasConceptRevisions: boolean }>({
    id,
    fields: {
      revisions(existing, { isReference }) {
        // A Revisions object carries no identity, so it is stored inline; the
        // modifier type still admits a reference, which is left alone.
        if (isReference(existing) || previousRevisionsCount === undefined) {
          return existing;
        }
        return {
          ...existing,
          count: Math.max(existing?.count ?? 0, previousRevisionsCount + 1),
        };
      },
      hasConceptRevisions(existing) {
        return appliesToConcept ? true : existing;
      },
    },
  });
};
